import { useMemo, useState, useCallback } from "react";
import * as Yup from "yup";
import { toast } from "@volca/ui/lib/toast";
import { SERVICE_TITAN_STATUSES } from "@/business/constants";
import {
  useGetServiceTitanCredentials,
  useUpsertServiceTitanCredentials,
} from "@/business/service-titan-query";
import { formatValidationErrors } from "@/core/api/errors";
import { ApiError } from "@/core/api/types";
import businessServiceTitanStatusConfiguration from "./business-service-titan-status-configuration";
import { SERVICE_TITAN_SHOW_OPTIONS } from "./constants";

export interface ServiceTitanFormValues {
  tenant_id: string;
  client_id: string;
  client_secret: string;
}

export type ServiceTitanShowOption =
  (typeof SERVICE_TITAN_SHOW_OPTIONS)[keyof typeof SERVICE_TITAN_SHOW_OPTIONS];

export interface UseServiceTitanIntegrationOptions {
  businessId?: string | null;
  onSuccess?: (response: { data?: { credentials_status?: string } }) => void;
  onError?: (error: Error) => void;
  showClientAndSecretKey?: ServiceTitanShowOption;
  hideTenantIdWhenEstablished?: boolean;
}

export const useServiceTitanIntegration = ({
  businessId,
  onSuccess,
  onError,
  showClientAndSecretKey = SERVICE_TITAN_SHOW_OPTIONS.SHOW_CLIENT_AND_SECRET,
  hideTenantIdWhenEstablished = false,
}: UseServiceTitanIntegrationOptions) => {
  const [hasConnectionError, setHasConnectionError] = useState(false);

  const { data: credentials, isLoading } =
    useGetServiceTitanCredentials(businessId);
  const { mutate: upsertCredentials, isPending } =
    useUpsertServiceTitanCredentials(businessId || null);

  const status =
    credentials?.credentials_status ||
    SERVICE_TITAN_STATUSES.awaiting_tenant_id;
  const config = businessServiceTitanStatusConfiguration[status];

  const initialValues = useMemo<ServiceTitanFormValues>(
    () => ({
      tenant_id: credentials?.tenant_id || "",
      client_id: credentials?.client_id || "",
      client_secret: credentials?.client_secret || "",
    }),
    [credentials],
  );

  const validationSchema = useMemo(() => {
    const baseSchema = Yup.object().shape({
      tenant_id: Yup.string().required("Tenant ID is required"),
    });

    if (!status) return baseSchema;

    const needsClientCredentials =
      config[showClientAndSecretKey] &&
      status !== SERVICE_TITAN_STATUSES.connection_established;

    return needsClientCredentials
      ? baseSchema.shape({
          client_id: Yup.string().required("Client ID is required"),
          client_secret: Yup.string().required("Client Secret is required"),
        })
      : baseSchema;
  }, [status, config, showClientAndSecretKey]);

  const handleSubmit = useCallback(
    (values: ServiceTitanFormValues) => {
      if (!businessId) return;

      const params = {
        id: credentials?.id,
        service_titan_credential: {
          tenant_id: values.tenant_id,
          client_id: values.client_id || null,
          client_secret: values.client_secret || null,
        },
      };

      const defaultOnSuccess = (resp: {
        data?: { credentials_status?: string };
      }) => {
        const nextStatus = resp.data?.credentials_status;
        const isComplete =
          nextStatus === SERVICE_TITAN_STATUSES.connection_established;
        const connectionFailed =
          nextStatus === SERVICE_TITAN_STATUSES.credentials_received;

        if (isComplete) {
          toast.success("ServiceTitan integration updated successfully");
          setHasConnectionError(false);
        } else if (connectionFailed) {
          setHasConnectionError(true);
          toast.error("Connection failed. Please check your credentials.");
        } else {
          toast.success("Saved. We're processing your information.");
          setHasConnectionError(false);
        }
      };

      const defaultOnError = (error: Error) => {
        let description = "Please try again";

        if (error instanceof ApiError && error.details) {
          description = formatValidationErrors(error.details);
        } else if (error instanceof Error) {
          description = error.message;
        }

        toast.error(
          `Failed to ${credentials?.id ? "update" : "create"} ServiceTitan credentials`,
          {
            description,
          },
        );
        setHasConnectionError(false);
      };

      upsertCredentials(params, {
        onSuccess: onSuccess || defaultOnSuccess,
        onError: onError || defaultOnError,
      });
    },
    [businessId, credentials?.id, upsertCredentials, onSuccess, onError],
  );

  const showTenantIdField =
    status !== SERVICE_TITAN_STATUSES.tenant_id_received &&
    (!hideTenantIdWhenEstablished ||
      status !== SERVICE_TITAN_STATUSES.connection_established);
  const showClientFields = config[showClientAndSecretKey] || false;
  const showErrorState =
    hasConnectionError ||
    status === SERVICE_TITAN_STATUSES.credentials_received;
  const showSuccessState =
    status === SERVICE_TITAN_STATUSES.connection_established;

  const isSubmitDisabled = useCallback(
    (values: ServiceTitanFormValues) => {
      const { tenant_id, client_id, client_secret } = values;

      if (!status) return false;

      const needsClientCredentials =
        config[showClientAndSecretKey] &&
        status !== SERVICE_TITAN_STATUSES.connection_established;

      if (status === SERVICE_TITAN_STATUSES.connection_established) {
        return (
          !tenant_id ||
          (tenant_id === credentials?.tenant_id &&
            client_id === credentials?.client_id &&
            client_secret === credentials?.client_secret)
        );
      }

      return needsClientCredentials
        ? !tenant_id || !client_id || !client_secret
        : !tenant_id;
    },
    [credentials, status, config, showClientAndSecretKey],
  );

  return {
    // Data
    credentials,
    status,
    config,
    initialValues,
    validationSchema,

    // State
    hasConnectionError,
    setHasConnectionError,
    isLoading,
    isPending,

    // Actions
    handleSubmit,

    // Computed UI state
    showTenantIdField,
    showClientFields,
    showErrorState,
    showSuccessState,
    isSubmitDisabled,
  };
};
